import { useState, useEffect, useMemo, useCallback } from 'react'
import { ChevronDown, ChevronRight, Loader2, BookOpen, X } from 'lucide-react'
import {
  getChangelogVisitsAdmin,
  getChangelogVisitTrendsAdmin,
  type ChangelogVisitAggregate,
  type ChangelogVisitTrends,
} from '../../../lib/api'
import { AnalyticsNav } from './AnalyticsOverview'
import PeriodSelector from '../../../components/analytics/PeriodSelector'
import TrendChart from '../../../components/analytics/TrendChart'
import { CHART_PALETTE } from '../../../lib/chart-colors'
import { cn } from '../../../lib/utils'

const TRENDS_PERIOD_OPTIONS = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
]

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function slugLabel(slug: string): string {
  return slug === 'index' ? 'Changelog index (/changelog)' : `Release ${slug}`
}

const SOURCE_LABELS: Record<string, string> = {
  direct: 'Direct',
  email: 'Email',
  push: 'Push notification',
  notification: 'In-app notification',
  slack: 'Slack',
  teams: 'Microsoft Teams',
  telegram: 'Telegram',
  whats_new: "What's New dialog",
  related_release: 'Related release link',
  internal_changelog: 'Within changelog',
  internal_guide: 'Guide cross-link',
  internal_home: 'Home page',
  internal_apps: 'Apps page',
  internal_other: 'Other internal page',
  external_other: 'Other external site',
  unknown: 'Unknown',
}

function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

// Sort key: 'index' first, then versions descending (v1.14 > v1.13 > ...).
function slugSortKey(slug: string): [number, number[]] {
  if (slug === 'index') return [-1, []]
  const parts = slug.replace(/^v/, '').split('.').map((n) => parseInt(n, 10) || 0)
  // Negate to get descending order via ascending sort.
  return [0, parts.map((p) => -p)]
}

export default function ChangelogVisits() {
  const [aggregates, setAggregates] = useState<ChangelogVisitAggregate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  // Trends chart state — independent fetch + period/slug filter.
  const [trendsPeriod, setTrendsPeriod] = useState('30d')
  const [trendsSlugFilter, setTrendsSlugFilter] = useState<string>('')
  const [trends, setTrends] = useState<ChangelogVisitTrends | null>(null)
  const [trendsLoading, setTrendsLoading] = useState(true)
  const [trendsError, setTrendsError] = useState<string | null>(null)

  useEffect(() => {
    getChangelogVisitsAdmin()
      .then((data) => setAggregates(data))
      .catch((err) => setError(err.message || 'Failed to load changelog visits'))
      .finally(() => setLoading(false))
  }, [])

  const loadTrends = useCallback(async () => {
    setTrendsLoading(true)
    setTrendsError(null)
    try {
      const data = await getChangelogVisitTrendsAdmin(
        trendsPeriod,
        trendsSlugFilter || undefined,
      )
      setTrends(data)
    } catch (err) {
      setTrendsError(err instanceof Error ? err.message : 'Failed to load trends')
    } finally {
      setTrendsLoading(false)
    }
  }, [trendsPeriod, trendsSlugFilter])

  useEffect(() => {
    loadTrends()
  }, [loadTrends])

  const sortedAggregates = useMemo(() => {
    return [...aggregates].sort((a, b) => {
      const [aHead, aTail] = slugSortKey(a.slug)
      const [bHead, bTail] = slugSortKey(b.slug)
      if (aHead !== bHead) return aHead - bHead
      for (let i = 0; i < Math.max(aTail.length, bTail.length); i++) {
        const av = aTail[i] ?? 0
        const bv = bTail[i] ?? 0
        if (av !== bv) return av - bv
      }
      return 0
    })
  }, [aggregates])

  const toggle = (slug: string) =>
    setExpanded((prev) => ({ ...prev, [slug]: !prev[slug] }))

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Changelog Visits
        </h1>
        <p className="text-muted-foreground">
          Tracks who has opened the changelog index and each release page.
          Total visits counts every page open; unique users counts distinct
          accounts.
        </p>
        <p className="text-xs text-muted-foreground/80 mt-2 max-w-3xl">
          <strong>About sources:</strong> visits from links we stamp with{' '}
          <code className="text-[11px]">?utm_source=…</code> (the What&apos;s
          New dialog, related-release links, future push/email announcements)
          are attributed reliably. Off-platform clicks from email clients or
          Slack typically arrive without a referrer because those services
          strip it for privacy, so they appear as <em>Direct</em> unless the
          link itself carried a UTM tag.
        </p>
      </div>

      {/* Sub-navigation */}
      <AnalyticsNav />

      {/* Trends chart — total visits over time + ranked-by-release bar */}
      <section className="mb-8 rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold">Visit trends</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Capped at 90 days — older per-visit events are pruned to keep
              the table small.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={trendsSlugFilter}
              onChange={(e) => setTrendsSlugFilter(e.target.value)}
              className="rounded-md border border-border bg-card px-2 py-1.5 text-sm"
            >
              <option value="">All releases</option>
              {sortedAggregates
                .filter((a) => a.slug !== 'index')
                .map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.slug}
                  </option>
                ))}
              <option value="index">Changelog index</option>
            </select>
            {trendsSlugFilter && (
              <button
                onClick={() => setTrendsSlugFilter('')}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                title="Clear filter"
              >
                <X className="w-3 h-3" />
                Clear
              </button>
            )}
            <PeriodSelector
              value={trendsPeriod}
              onChange={setTrendsPeriod}
              options={TRENDS_PERIOD_OPTIONS}
            />
          </div>
        </div>

        {trendsLoading && !trends ? (
          <div className="flex items-center gap-2 text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading trends...
          </div>
        ) : trendsError ? (
          <p className="text-red-400">{trendsError}</p>
        ) : (
          <>
            <TrendChart
              data={trends?.buckets ?? []}
              lines={[
                {
                  dataKey: 'visits',
                  label: trendsSlugFilter
                    ? `Visits — ${trendsSlugFilter}`
                    : 'Total visits',
                  color: CHART_PALETTE[0],
                },
              ]}
              height={260}
            />
            {/* Ranked-by-release bar list — clickable to filter the chart */}
            {trends && trends.by_release.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                  Most-read in this period
                </div>
                <ul className="space-y-1.5">
                  {(() => {
                    const max = Math.max(
                      ...trends.by_release.map((r) => r.visits),
                      1,
                    )
                    return trends.by_release.map((r) => {
                      const pct = Math.round((r.visits / max) * 100)
                      const active = trendsSlugFilter === r.slug
                      return (
                        <li key={r.slug}>
                          <button
                            onClick={() =>
                              setTrendsSlugFilter(active ? '' : r.slug)
                            }
                            className={cn(
                              'w-full grid grid-cols-[7rem_1fr_3rem] items-center gap-3 text-left rounded-md px-2 py-1.5 hover:bg-secondary/40 transition-colors',
                              active && 'bg-secondary/40',
                            )}
                            title={
                              active
                                ? `Showing ${r.slug} — click to clear`
                                : `Filter chart to ${r.slug}`
                            }
                          >
                            <span
                              className={cn(
                                'text-sm font-mono truncate',
                                active && 'text-foreground font-semibold',
                              )}
                            >
                              {r.slug === 'index' ? 'index' : r.slug}
                            </span>
                            <span className="relative h-2 rounded-full bg-secondary/40 overflow-hidden">
                              <span
                                className="absolute inset-y-0 left-0 rounded-full"
                                style={{
                                  width: `${pct}%`,
                                  backgroundColor: CHART_PALETTE[0],
                                  opacity: active ? 0.9 : 0.5,
                                }}
                              />
                            </span>
                            <span className="text-sm tabular-nums text-right text-muted-foreground">
                              {r.visits}
                            </span>
                          </button>
                        </li>
                      )
                    })
                  })()}
                </ul>
              </div>
            )}
            {trends && trends.by_release.length === 0 && (
              <p className="text-sm text-muted-foreground mt-4">
                No visits recorded in this period yet.
              </p>
            )}
          </>
        )}
      </section>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading...
        </div>
      ) : error ? (
        <p className="text-red-400">{error}</p>
      ) : sortedAggregates.length === 0 ? (
        <p className="text-muted-foreground">No visits recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {sortedAggregates.map((agg) => {
            const isExpanded = expanded[agg.slug]
            return (
              <div
                key={agg.slug}
                className="border border-border rounded-lg bg-card overflow-hidden"
              >
                <button
                  onClick={() => toggle(agg.slug)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-secondary/40 transition-colors text-left"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{slugLabel(agg.slug)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {agg.unique_users} unique{' '}
                      {agg.unique_users === 1 ? 'user' : 'users'} · {agg.total_visits} total{' '}
                      {agg.total_visits === 1 ? 'visit' : 'visits'}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-border">
                    {/* Source breakdown — empty until events accrue post-deploy */}
                    {agg.sources.length > 0 && (
                      <div className="px-4 py-3 border-b border-border bg-secondary/10">
                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                          Where visits came from
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {agg.sources.map((s) => (
                            <span
                              key={s.source}
                              className="inline-flex items-baseline gap-2 px-2.5 py-1 rounded-md bg-secondary/40 border border-border text-xs"
                            >
                              <span className="font-medium">{sourceLabel(s.source)}</span>
                              <span className="text-muted-foreground">
                                {s.visits} {s.visits === 1 ? 'visit' : 'visits'}
                                {s.unique_users !== s.visits && ` · ${s.unique_users} ${s.unique_users === 1 ? 'user' : 'users'}`}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/30 text-xs text-muted-foreground uppercase tracking-wider">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">User</th>
                          <th className="text-left px-4 py-2 font-medium">Source</th>
                          <th className="text-left px-4 py-2 font-medium">Last visit</th>
                          <th className="text-right px-4 py-2 font-medium">Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agg.viewers.map((v) => (
                          <tr
                            key={v.user_id}
                            className="border-t border-border/50 hover:bg-secondary/20"
                          >
                            <td className="px-4 py-2">
                              <div className="font-medium">{v.name || v.email}</div>
                              <div
                                className={cn(
                                  'text-xs text-muted-foreground',
                                  v.is_admin && 'text-amber-400/80'
                                )}
                              >
                                {v.email}
                                {v.is_admin && ' · admin'}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                              {v.last_source ? sourceLabel(v.last_source) : '—'}
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">
                              {formatDateTime(v.last_visited_at)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono">
                              {v.visit_count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
